export type DayScheduleInternal = {
  start_time: string | null
  end_time: string | null
}

export type WeeklyScheduleInternal = Record<string, DayScheduleInternal>

export const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

export function createDefaultSchedule(): WeeklyScheduleInternal {
  return {
    monday: { start_time: '09:00', end_time: '17:00' },
    tuesday: { start_time: '09:00', end_time: '17:00' },
    wednesday: { start_time: '09:00', end_time: '17:00' },
    thursday: { start_time: '09:00', end_time: '17:00' },
    friday: { start_time: '09:00', end_time: '17:00' },
    saturday: { start_time: null, end_time: null },
    sunday: { start_time: null, end_time: null },
  }
}

export function toInternalSchedule(
  apiSchedule: Record<string, unknown> | null | undefined,
): WeeklyScheduleInternal {
  if (!apiSchedule) return createDefaultSchedule()
  const internal: WeeklyScheduleInternal = {}
  for (const day in apiSchedule) {
    const dayObj = apiSchedule[day] as Record<string, string | null> | undefined
    if (!dayObj) continue
    const {
      startTime = null,
      endTime = null,
      start_time = null,
      end_time = null,
    } = dayObj
    internal[day] = {
      start_time: start_time ?? startTime ?? null,
      end_time: end_time ?? endTime ?? null,
    }
  }
  const base = createDefaultSchedule()
  for (const d in base) {
    if (!internal[d]) internal[d] = { start_time: null, end_time: null }
  }
  return internal
}

export function toApiSchedule(
  internalSchedule: WeeklyScheduleInternal | null | undefined,
): Record<string, { start_time: string, end_time: string }> | null {
  if (!internalSchedule) return null
  const api: Record<string, { start_time: string, end_time: string }> = {}
  for (const day in internalSchedule) {
    const dayObj = internalSchedule[day]
    if (!dayObj) continue
    const start = dayObj.start_time ?? null
    const end = dayObj.end_time ?? null
    if (start && end) {
      const startApi = start.length >= 8 ? start : `${start}:00`
      // API end times are inclusive; a 23:59 display end means "through end of day".
      const endApi = end.slice(0, 5) === '23:59' ? '23:59:59' : end.length >= 8 ? end : `${end}:00`
      api[day] = { start_time: startApi, end_time: endApi }
    }
  }
  return Object.keys(api).length ? api : null
}

export function availabilitySchedulesFromApi(
  raw: Record<string, unknown> | unknown[] | null | undefined,
) {
  if (raw == null) {
    return { scheduleActive: false, schedules: [createDefaultSchedule()] }
  }
  if (Array.isArray(raw)) {
    if (raw.length === 0) {
      return { scheduleActive: true, schedules: [createDefaultSchedule()] }
    }
    return {
      scheduleActive: true,
      schedules: raw.map(entry =>
        toInternalSchedule(entry as Record<string, unknown>),
      ),
    }
  }
  return {
    scheduleActive: true,
    schedules: [toInternalSchedule(raw)],
  }
}

export function availabilitySchedulesToApi(
  scheduleActive: boolean,
  schedules: WeeklyScheduleInternal[],
) {
  if (!scheduleActive) return null
  const api = schedules
    .map(schedule => toApiSchedule(schedule))
    .filter(
      (entry): entry is Record<string, { start_time: string, end_time: string }> =>
        entry != null,
    )
  return api.length ? api : null
}

/** @deprecated Use availabilitySchedulesFromApi */
export function availabilityScheduleFromApi(
  apiSchedule: Record<string, unknown> | null | undefined,
) {
  const result = availabilitySchedulesFromApi(apiSchedule)
  return {
    scheduleActive: result.scheduleActive,
    schedule: result.schedules[0] ?? createDefaultSchedule(),
  }
}

/** @deprecated Use availabilitySchedulesToApi */
export function availabilityScheduleToApi(
  scheduleActive: boolean,
  schedule: WeeklyScheduleInternal,
) {
  return availabilitySchedulesToApi(
    scheduleActive,
    scheduleActive ? [schedule] : [],
  )
}

export function hasAnyAvailabilitySchedule(
  schedules: WeeklyScheduleInternal[],
): boolean {
  if (!Array.isArray(schedules)) return false
  for (const schedule of schedules) {
    for (const day of WEEKDAYS) {
      const slot = schedule[day]
      if (slot?.start_time && slot?.end_time) return true
    }
  }
  return false
}

export function parseTimeToMinutes(t: string | null | undefined): number | null {
  if (t == null || typeof t !== 'string') return null
  const parts = t.trim().split(':')
  const h = parseInt(parts[0] ?? '', 10)
  const m = parts.length > 1 ? parseInt(parts[1] ?? '', 10) : 0
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  return h * 60 + m
}

export function minutesToDisplayTime(minutes: number | null): string | null {
  if (minutes == null || !Number.isFinite(minutes)) return null
  const capped = Math.min(24 * 60 - 1, Math.max(0, Math.round(minutes)))
  const h = Math.floor(capped / 60)
  const m = capped % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export function createNextAvailabilitySchedule(
  schedules: WeeklyScheduleInternal[],
): WeeklyScheduleInternal {
  if (!Array.isArray(schedules) || schedules.length === 0) {
    return createDefaultSchedule()
  }
  const previous = schedules[schedules.length - 1] ?? createDefaultSchedule()
  const schedule: WeeklyScheduleInternal = {}
  for (const day of WEEKDAYS) {
    const slot = previous[day]
    if (!slot) {
      schedule[day] = { start_time: null, end_time: null }
      continue
    }
    const end = slot.end_time ?? null
    if (!end) {
      schedule[day] = { start_time: null, end_time: null }
      continue
    }
    const startMin = parseTimeToMinutes(end)
    if (startMin == null) {
      schedule[day] = { start_time: null, end_time: null }
      continue
    }
    const endMin = Math.min(24 * 60 - 1, startMin + 2 * 60)
    schedule[day] = {
      start_time: end,
      end_time: minutesToDisplayTime(endMin),
    }
  }
  return schedule
}

export function expandDayIntervalSegments(
  startMin: number,
  endMin: number,
): Array<[number, number]> {
  if (startMin < endMin) return [[startMin, endMin]]
  if (startMin > endMin) return [[startMin, 24 * 60], [0, endMin]]
  return []
}

export function validateAvailabilitySchedulesNoOverlap(
  schedules: WeeklyScheduleInternal[],
): string | null {
  if (!Array.isArray(schedules) || schedules.length < 2) return null
  for (const day of WEEKDAYS) {
    const segments: Array<[number, number]> = []
    for (const schedule of schedules) {
      const daySlot = schedule[day]
      if (!daySlot) continue
      const start = daySlot.start_time ?? null
      const end = daySlot.end_time ?? null
      if (start == null || end == null) continue
      const startMin = parseTimeToMinutes(start)
      const endMin = parseTimeToMinutes(end)
      if (startMin == null || endMin == null) continue
      segments.push(...expandDayIntervalSegments(startMin, endMin))
    }
    segments.sort((a, b) => a[0] - b[0])
    for (let i = 0; i < segments.length - 1; i++) {
      const current = segments[i]
      const next = segments[i + 1]
      if (current && next && current[1] > next[0]) {
        return 'Time windows must not overlap.'
      }
    }
  }
  return null
}

export type ScheduledGreetingInternal = {
  message: string
  label: string
  schedule: WeeklyScheduleInternal
}

export function createDefaultScheduledGreeting(): ScheduledGreetingInternal {
  const schedule: WeeklyScheduleInternal = {}
  for (const day of WEEKDAYS) {
    schedule[day] = { start_time: null, end_time: null }
  }
  return {
    message: '',
    label: '',
    schedule,
  }
}

export function scheduledGreetingsFromApi(
  raw: Array<{
    message?: string | null
    label?: string | null
    schedule?: Record<string, unknown> | null
  }> | null | undefined,
): ScheduledGreetingInternal[] {
  if (!Array.isArray(raw) || raw.length === 0) return []
  return raw.map(entry => ({
    message: typeof entry?.message === 'string' ? entry.message : '',
    label: typeof entry?.label === 'string' ? entry.label : '',
    schedule: toInternalSchedule(entry?.schedule ?? undefined),
  }))
}

export function scheduledGreetingsToApi(
  entries: ScheduledGreetingInternal[],
): Array<{
  message: string
  label: string | null
  schedule: Record<string, { start_time: string, end_time: string }>
}> {
  if (!Array.isArray(entries)) return []
  return entries
    .map((entry) => {
      const schedule = toApiSchedule(entry.schedule)
      if (!schedule) return null
      const message = (entry.message || '').trim()
      if (!message) return null
      const label = (entry.label || '').trim()
      return {
        message,
        label: label || null,
        schedule,
      }
    })
    .filter((entry): entry is {
      message: string
      label: string | null
      schedule: Record<string, { start_time: string, end_time: string }>
    } => entry != null)
}

export function hasAnyScheduledGreetingWindow(
  entries: ScheduledGreetingInternal[],
): boolean {
  if (!Array.isArray(entries)) return false
  return entries.every(entry => hasAnyAvailabilitySchedule([entry.schedule]))
}

export function validateScheduledGreetingsNoOverlap(
  entries: ScheduledGreetingInternal[],
): string | null {
  if (!Array.isArray(entries) || entries.length < 2) return null
  return validateAvailabilitySchedulesNoOverlap(entries.map(entry => entry.schedule))
}
